import { useState, useEffect, useRef } from 'react'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { saveFieldEdit } from '../data/metaApi.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useUIStore } from '../store/uiStore.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { fieldEditKey, pillarIndexByName, timeAgo } from '../data/metaKeys.js'

/**
 * EditableValue — click to edit a single field on a goal or sub-goal.
 *
 * Usage:
 *   <EditableValue
 *     pillarName="Revenue"
 *     goalIndex={0}
 *     subgoalIndex={2}
 *     field="current"
 *     value={10000}
 *     unit="$"
 *     formatter={(v) => fmt(v, '$')}
 *   />
 *
 * Behavior:
 *   - Renders as plain text until the user clicks (or in edit mode)
 *   - On click: becomes a focused input
 *   - Enter or blur: saves via useUpdateGoal (optimistic → server)
 *   - Esc: cancels and reverts to the stored value
 *
 * Requires edit mode (from uiStore) to be turned on — otherwise it's read-only.
 */
export default function EditableValue({
  pillarName,
  goalIndex,
  subgoalIndex,
  field,
  value,
  unit,
  formatter,
  inputType = 'number',
  placeholder = '',
  multiline = false,
  readonly = false,
  readonlyReason = 'Auto-synced from HubSpot — edit in HubSpot to change',
  style = {},
}) {
  const currentUser = useUIStore((s) => s.currentUser)
  const setEditingGoalKey = useUIStore((s) => s.setEditingGoalKey)
  const clearEditingGoalKey = useUIStore((s) => s.clearEditingGoalKey)
  const [isEditing, setIsEditing] = useState(false)
  // Archive view forces every field to read-only — no edit menus or
  // focus rings, no save mutations. Per-field `readonly` prop still
  // applies in live view (HubSpot-synced current values, etc.).
  const viewingArchive = useIsReadOnly()
  const canEdit = !readonly && !viewingArchive

  // While editing, claim this goal in the UI store so the GoalCard
  // enters presence for it — other users see "Ricky is editing this goal".
  useEffect(() => {
    if (!isEditing || !pillarName || goalIndex === undefined) return
    const key = `${pillarName}:${goalIndex}`
    setEditingGoalKey(key)
    return () => clearEditingGoalKey()
  }, [isEditing, pillarName, goalIndex, setEditingGoalKey, clearEditingGoalKey])

  // Attribution lookup
  const { data: goalsData } = useGoalsData()
  const { data: meta } = useMetaData()
  const pi = pillarIndexByName(goalsData || [], pillarName)
  const editKey = pi >= 0 ? fieldEditKey(pi, goalIndex, field, subgoalIndex) : null
  const attribution = editKey ? meta.fieldEdits?.[editKey] : null
  const [draft, setDraft] = useState(String(value ?? ''))
  const inputRef = useRef(null)
  const mutation = useUpdateGoal()

  // Keep draft in sync with incoming value when not editing
  useEffect(() => {
    if (!isEditing) setDraft(String(value ?? ''))
  }, [value, isEditing])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select?.()
    }
  }, [isEditing])

  const save = () => {
    // Normalize value based on input type
    let newValue = draft
    if (inputType === 'number') {
      newValue = draft === '' ? 0 : Number(draft)
      if (Number.isNaN(newValue)) {
        setDraft(String(value ?? ''))
        setIsEditing(false)
        return
      }
    }
    if (newValue === value) {
      setIsEditing(false)
      return
    }
    mutation.mutate({
      pillarName,
      goalIndex,
      subgoalIndex,
      field,
      value: newValue,
    })
    // Record attribution via the dedicated /meta/fieldEdits partial
    // endpoint. Switched off the full-meta POST path so two editors
    // saving different fields at the same time no longer clobber each
    // other's attribution rows (or anything else in meta).
    //
    // Archive guard here too — saveFieldEdit is a plain fetch, not a
    // mutation hook, so it bypasses the hook-level archive wrapper.
    if (editKey && !useArchiveViewStore.getState().active) {
      saveFieldEdit({
        key: editKey,
        edit: {
          by: currentUser || 'Unknown',
          at: new Date().toISOString(),
        },
      }).catch((err) => {
        // Attribution is best-effort — the actual value already saved via
        // mutation.mutate above. If attribution fails, log and move on;
        // we don't want a flaky network call to block the edit itself.
        console.warn('[editable-value] fieldEdit attribution save failed', err)
      })
    }
    setIsEditing(false)
  }

  const cancel = () => {
    setDraft(String(value ?? ''))
    setIsEditing(false)
  }

  // Read-only display (shown when not in edit mode, or when readonly, or when not actively editing)
  if (!canEdit || !isEditing) {
    const isEmpty = value === null || value === undefined || value === ''
    let display
    if (isEmpty) {
      // Empty: show placeholder when we can edit, else show em-dash
      display = canEdit ? (placeholder || 'Click to add…') : '—'
    } else {
      display = formatter ? formatter(value) : value
    }

    const isItalicHint = isEmpty && canEdit
    // Readonly fields: subtle visual cue that editing is intentionally locked
    const showLockHint = readonly

    return (
      <span
        onClick={() => canEdit && setIsEditing(true)}
        style={{
          cursor: canEdit ? 'pointer' : showLockHint ? 'not-allowed' : 'default',
          borderRadius: 4,
          padding: canEdit ? '2px 6px' : 0,
          margin: canEdit ? '-2px -6px' : 0,
          transition: 'background 0.1s, outline-color 0.1s',
          display: 'inline-block',
          minWidth: canEdit && isEmpty ? 100 : undefined,
          ...(canEdit && {
            outline: isEmpty ? '1px dashed var(--border-strong)' : '1px dashed transparent',
          }),
          ...(isItalicHint && {
            fontStyle: 'italic',
            color: 'var(--muted-light)',
            fontWeight: 500,
          }),
          ...(showLockHint && {
            opacity: 0.85,
          }),
          ...style,
        }}
        onMouseEnter={(e) => {
          if (canEdit) {
            e.currentTarget.style.background = '#eff6ff'
            e.currentTarget.style.outline = '1px dashed var(--accent)'
          }
        }}
        onMouseLeave={(e) => {
          if (canEdit) {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.outline = isEmpty
              ? '1px dashed var(--border-strong)'
              : '1px dashed transparent'
          }
        }}
        title={
          canEdit
            ? attribution
              ? `Click to edit · last edited by ${attribution.by} · ${timeAgo(attribution.at)}`
              : 'Click to edit'
            : showLockHint
              ? `🔒 ${readonlyReason}`
              : attribution
                ? `Last edited by ${attribution.by} · ${timeAgo(attribution.at)}`
                : ''
        }
      >
        {display}
      </span>
    )
  }

  // Editing
  const commonInputStyle = {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    fontFamily: 'inherit',
    color: 'var(--text-secondary)',
    border: '1px solid var(--accent)',
    borderRadius: 4,
    padding: '2px 6px',
    background: '#fff',
    outline: 'none',
    minWidth: 40,
  }

  if (multiline) {
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              save()
            }
          }}
          placeholder={placeholder || 'Type here…'}
          style={{
            ...commonInputStyle,
            minHeight: 60,
            width: '100%',
            resize: 'vertical',
            lineHeight: 1.5,
            ...style,
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: 8,
            bottom: -16,
            fontSize: 10,
            color: 'var(--muted-light)',
            fontStyle: 'italic',
            fontWeight: 500,
            pointerEvents: 'none',
          }}
        >
          click away to save · Esc to cancel
        </div>
      </div>
    )
  }

  return (
    <input
      ref={inputRef}
      type={inputType === 'number' ? 'text' : inputType}
      inputMode={inputType === 'number' ? 'numeric' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Escape') cancel()
        if (e.key === 'Enter') save()
      }}
      placeholder={placeholder}
      style={{ ...commonInputStyle, ...style }}
    />
  )
}
